/**
 * Invoices (Fakturor) — Phase 21, Task 21.11. PRD 10.4, 10.8, 10.10, 10.12.
 *
 * The list, the filters and the accounts-receivable aging view. The document
 * itself lives in InvoiceDetailPage.
 *
 * Two rules govern everything on this page:
 *
 * 1. STATUS IS PRESENTED HONESTLY. The stored `status === "sent"` means
 *    finalised and locked, NOT delivered — `sent_at` is written only when a
 *    send is confirmed (Task 21.12). An issued invoice that nobody has emailed
 *    is shown as "Issued", never as "Sent". See `deriveInvoiceState`.
 *
 * 2. NO MONEY IS COMPUTED HERE. Every figure shown is one the invoicing engine
 *    already produced and stored. The aging view sums stored invoice totals
 *    into buckets — an aggregate over documents, never a re-derivation of one.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    getInvoices,
    getClients,
    createInvoice,
    downloadInvoicePdf,
    markInvoiceSent,
} from '../../lib/api';
import { getApiErrorMessage } from '../../lib/errors';
import { formatDate } from '../../lib/format-utils';
import {
    AGING_BUCKETS,
    buildAging,
    deriveInvoiceState,
    INVOICE_STATE,
    downloadBlobResponse,
    formatAmountList,
    formatMoney,
    invoiceStateLabelKey,
    invoiceStateVariant,
    sortInvoices,
    todayIso,
} from './invoice-utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    Plus,
    Download,
    FileText,
    Loader2,
    ChevronLeft,
    ChevronRight,
    FilePlus,
    CheckCircle2,
    Coins,
    FileSpreadsheet,
} from 'lucide-react';
import { SummaryCard, SummaryCardRow } from '../../components/summary-cards';

// Sentinel for "no filter". A radix Select item cannot carry an empty value,
// and an empty string must never be sent to the backend either.
const ANY = 'all';

const PAGE_SIZE = 50;

// The aging view reads EVERY outstanding invoice, so it pages rather than
// taking one big slice. 500 is _MAX_LIST_LIMIT on the backend — the largest
// page it will serve, which keeps the request count as low as it can be.
const AGING_PAGE_SIZE = 500;

// A ceiling on the paging loop: 20 pages × 500 = 10 000 outstanding invoices
// per status. It exists so a backend that kept returning full pages could not
// spin this forever, NOT as an expected limit — and if it is ever hit the view
// says out loud that it is incomplete rather than quietly under-reporting.
const AGING_MAX_PAGES = 20;

// The statuses the backend accepts on ?status=. These are STORED statuses, not
// the presented states — "sent" here means finalised, and the option is
// labelled accordingly (invoices.filter_status_sent).
const STORED_STATUSES = ['draft', 'sent', 'partly_paid', 'paid', 'overdue', 'credited'];

const DOC_TYPES = ['invoice', 'credit_note'];

// Five years back is the Swedish bookkeeping retention horizon an accountant
// actually reaches for; anything older is found by narrowing to its year.
function yearOptions() {
    const current = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, index) => current - index);
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export default function InvoicesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [outstanding, setOutstanding] = useState([]);

    /**
     * How much the aging panel actually knows. One state rather than a
     * boolean, because "we are owed nothing" and "we do not know what we are
     * owed" are different sentences and only one of them may be printed.
     *
     *   'loading'   — the read has not come back. Nothing is asserted yet.
     *   'ready'     — the buckets and total are the whole receivable.
     *   'truncated' — the paging loop hit its ceiling; the figures are a floor.
     *   'failed'    — the read threw. There are no figures at all.
     *
     * It starts at 'loading' on purpose: an empty `outstanding` array is the
     * initial state as well as the genuinely-nothing-outstanding state, and
     * without this the panel claims every invoice is settled for the moment
     * before the first response lands.
     */
    const [agingStatus, setAgingStatus] = useState('loading');
    const [downloadingId, setDownloadingId] = useState(null);
    const [markingSentId, setMarkingSentId] = useState(null);
    const [markSentTarget, setMarkSentTarget] = useState(null);

    const handleMarkSent = async (invoice) => {
        setMarkingSentId(invoice.id);
        try {
            const res = await markInvoiceSent(invoice.id);
            setInvoices((prev) => prev.map((inv) => (inv.id === invoice.id ? res.data : inv)));
            toast.success(t('invoices.marked_as_sent_toast', { number: invoice.invoice_number || '' }));
        } catch (err) {
            toast.error(getApiErrorMessage(err, t('invoices.failed_mark_sent')));
        } finally {
            setMarkingSentId(null);
        }
    };

    const [statusFilter, setStatusFilter] = useState(ANY);
    const [clientFilter, setClientFilter] = useState(ANY);
    const [docTypeFilter, setDocTypeFilter] = useState(ANY);
    const [yearFilter, setYearFilter] = useState(ANY);
    const [monthFilter, setMonthFilter] = useState(ANY);
    const [page, setPage] = useState(0);

    const [createOpen, setCreateOpen] = useState(false);
    const [createClientId, setCreateClientId] = useState('');
    const [creating, setCreating] = useState(false);

    const today = todayIso();

    const filtersActive =
        statusFilter !== ANY ||
        clientFilter !== ANY ||
        docTypeFilter !== ANY ||
        yearFilter !== ANY;

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
            if (statusFilter !== ANY) params.status = statusFilter;
            if (clientFilter !== ANY) params.client_id = clientFilter;
            if (docTypeFilter !== ANY) params.doc_type = docTypeFilter;
            if (yearFilter !== ANY) {
                params.year = Number(yearFilter);
                // `month` without `year` is a 400 (error.invoices.invalid_period),
                // so the month is only ever sent alongside its year.
                if (monthFilter !== ANY) params.month = Number(monthFilter);
            }
            const res = await getInvoices(params);
            setInvoices(res.data || []);
        } catch (error) {
            setInvoices([]);
            toast.error(getApiErrorMessage(error, t('invoices.failed_load')));
        } finally {
            setLoading(false);
        }
    }, [statusFilter, clientFilter, docTypeFilter, yearFilter, monthFilter, page, t]);

    /**
     * The receivables view is deliberately NOT the filtered list: "who owes us
     * money" is a question about the whole tenant, and answering it from
     * whatever the user last filtered to would silently under-report.
     *
     * Outstanding is exactly the three stored statuses that are neither a
     * draft, nor settled, nor reversed — the backend's `status` filter takes
     * one value, so it is three reads rather than one over-wide one.
     *
     * EACH STATUS IS PAGED TO EXHAUSTION. A single 500-row request would drop
     * every invoice past the 500th out of the buckets AND out of the total
     * with nothing on screen to say so — and a receivables total that is
     * quietly short is worse than no total at all, because it is acted on.
     * So each status is read page by page until a short page proves the end,
     * and if the page ceiling is reached anyway the panel says the figures are
     * incomplete.
     *
     * A FAILED READ IS THE SAME BUG WITH DIFFERENT ARITHMETIC. Clearing the
     * rows and saying nothing left the panel indistinguishable from a
     * successful read that found nothing — so it printed "every issued invoice
     * is settled" at an admin who might be owed a fortune. A silently short
     * total and a silently empty one are equally acted upon. The failure is
     * therefore recorded in `agingStatus` and shown, and the buckets are not
     * drawn at all: partial figures under a "could not load" heading would
     * still invite someone to read them as the answer.
     */
    const fetchOutstanding = useCallback(async () => {
        const readAllOfStatus = async (status) => {
            const collected = [];
            for (let index = 0; index < AGING_MAX_PAGES; index += 1) {
                const res = await getInvoices({
                    status,
                    limit: AGING_PAGE_SIZE,
                    skip: index * AGING_PAGE_SIZE,
                });
                const batch = res.data || [];
                collected.push(...batch);
                // A page shorter than the limit is the end of this status.
                if (batch.length < AGING_PAGE_SIZE) return { rows: collected, complete: true };
            }
            return { rows: collected, complete: false };
        };

        setAgingStatus('loading');
        try {
            const results = await Promise.all(
                ['sent', 'partly_paid', 'overdue'].map(readAllOfStatus)
            );
            setOutstanding(results.flatMap((result) => result.rows));
            setAgingStatus(
                results.some((result) => !result.complete) ? 'truncated' : 'ready'
            );
        } catch (error) {
            // The aging panel is a summary; failing to build it must not take
            // the invoice list down with it — but it must not be passed off as
            // an answer either.
            setOutstanding([]);
            setAgingStatus('failed');
        }
    }, []);

    const fetchClients = useCallback(async () => {
        try {
            const res = await getClients({ include_inactive: true });
            setClients(res.data || []);
        } catch (error) {
            setClients([]);
        }
    }, []);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
    useEffect(() => { fetchOutstanding(); }, [fetchOutstanding]);
    useEffect(() => { fetchClients(); }, [fetchClients]);

    const rows = useMemo(() => sortInvoices(invoices), [invoices]);
    const aging = useMemo(() => buildAging(outstanding, today), [outstanding, today]);

    /**
     * Figures for THE INVOICES CURRENTLY LISTED — the filters above, one page
     * of results — and nothing wider (Task 22.7).
     *
     * THE TRAP THIS DELIBERATELY AVOIDS. The aging panel on this same screen
     * is cross-month on purpose: an unpaid March invoice must still be visible
     * in September. Summarising the filtered list beside it with anything that
     * reads as "what we are owed" would put two different answers to one
     * question on one page — and the smaller, narrower one would be the one
     * sitting next to the list, which is the one people read.
     *
     * So this row deliberately reports dimensions the receivable does NOT
     * cover: how many invoices are listed, how many of them are drafts or
     * settled (neither is outstanding at all), and what those documents were
     * invoiced FOR — `total_incl_vat`, the invoiced value, never
     * `amount_due`. The aging panel keeps sole ownership of the balance.
     *
     * Totals are per currency and summed in integer minor units, for the same
     * reason buildAging does it: adding EUR to SEK produces a number that
     * means nothing, and a column of öre drifts in binary floating point.
     *
     * A CREDIT NOTE SUBTRACTS, and is left to. `build_credit_note_lines`
     * negates the quantity, so a credit note's stored `total_incl_vat` is
     * already the exact negative of what it gives back. Summing it as it
     * stands is what makes "invoiced in this view" mean the net of what was
     * billed and what was credited; special-casing the sign here would print
     * a figure that double-counts a reversal.
     */
    const viewSummary = useMemo(() => {
        const minorByCurrency = {};
        let drafts = 0;
        let settled = 0;
        for (const invoice of rows) {
            const currency = (invoice.currency || 'SEK').toUpperCase();
            const minor = Math.round((Number(invoice.total_incl_vat) || 0) * 100);
            minorByCurrency[currency] = (minorByCurrency[currency] || 0) + minor;
            const state = deriveInvoiceState(invoice, today);
            if (state === INVOICE_STATE.DRAFT) drafts += 1;
            if (state === INVOICE_STATE.PAID) settled += 1;
        }
        return {
            count: rows.length,
            drafts,
            settled,
            amounts: Object.entries(minorByCurrency)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([currency, minor]) => ({ currency, amount: minor / 100 })),
        };
    }, [rows, today]);

    const activeClients = useMemo(() => clients.filter((c) => c.is_active), [clients]);
    const clientNameById = useMemo(() => {
        const map = {};
        for (const client of clients) map[client.id] = client.name;
        return map;
    }, [clients]);

    // The snapshot is the invoice's own record of who it was billed to and is
    // preferred over the live client name — an issued invoice must not change
    // because a customer was renamed afterwards (Principle 2).
    const displayClientName = (invoice) =>
        invoice.client_snapshot?.name || clientNameById[invoice.client_id] || '—';

    const resetFilters = () => {
        setStatusFilter(ANY);
        setClientFilter(ANY);
        setDocTypeFilter(ANY);
        setYearFilter(ANY);
        setMonthFilter(ANY);
        setPage(0);
    };

    const onFilterChange = (setter) => (value) => {
        setter(value);
        setPage(0);
    };

    const onYearChange = (value) => {
        setYearFilter(value);
        // Dropping the year would leave a month filter that the backend
        // refuses, so it is cleared with it.
        if (value === ANY) setMonthFilter(ANY);
        setPage(0);
    };

    const handleDownload = async (invoice) => {
        setDownloadingId(invoice.id);
        try {
            const response = await downloadInvoicePdf(invoice.id);
            downloadBlobResponse(response, `${invoice.invoice_number || 'draft'}.pdf`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_download')));
        } finally {
            setDownloadingId(null);
        }
    };

    const handleCreate = async () => {
        if (!createClientId) {
            toast.error(t('invoices.select_client'));
            return;
        }
        setCreating(true);
        try {
            // A blank draft dated today. `due_date` is deliberately left unset:
            // it is derived from the customer's payment terms at issue, and
            // guessing it here would put a number on the document that nobody
            // chose.
            const res = await createInvoice({
                client_id: createClientId,
                invoice_date: today,
                lines: [],
            });
            setCreateOpen(false);
            setCreateClientId('');
            toast.success(t('invoices.draft_created'));
            navigate(`/dashboard/invoices/${res.data.id}`);
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('invoices.failed_create')));
        } finally {
            setCreating(false);
        }
    };

    const StatusBadge = ({ invoice }) => {
        const state = deriveInvoiceState(invoice, today);
        return (
            <Badge variant={invoiceStateVariant(state)} data-testid={`invoice-status-${invoice.id}`}>
                {t(invoiceStateLabelKey(state))}
            </Badge>
        );
    };

    const numberCell = (invoice) =>
        invoice.invoice_number || t('invoices.no_number_yet');

    return (
        <div className="space-y-6" data-testid="invoices-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">
                        {t('invoices.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('invoices.subtitle')}</p>
                </div>
                {/* Two doors, both correct, labelled so the difference is
                    visible before the click (Task 22.8).

                    "New invoice" is the MANUAL path — a blank draft for a
                    fixed-price or ad-hoc invoice with no time behind it. It
                    deliberately does not populate projects, and it stays.

                    Everything billed FROM TIME AND EXPENSES starts at the
                    Billing Report, whose customer view generates a fully
                    populated draft in one click. That path already existed
                    and worked; nothing on this page pointed at it, so people
                    reached for the manual door and found it empty. This is a
                    signpost, not a redirect. */}
                <div className="flex flex-col items-start gap-2 sm:items-end">
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            className="uppercase tracking-wider"
                            onClick={() => navigate('/dashboard/billing')}
                            data-testid="invoice-from-billing-btn"
                        >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            {t('invoices.from_billing_action')}
                        </Button>
                        <Button
                            className="uppercase tracking-wider"
                            onClick={() => setCreateOpen(true)}
                            data-testid="new-invoice-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            {t('invoices.new_invoice')}
                        </Button>
                    </div>
                    <p
                        className="text-xs text-muted-foreground max-w-sm sm:text-right"
                        data-testid="invoice-from-billing-hint"
                    >
                        {t('invoices.from_billing_hint')}
                    </p>
                </div>
            </div>

            {/* Accounts receivable aging (PRD 10.12) */}
            <Card className="border border-border shadow-none" data-testid="invoices-aging">
                <CardContent className="p-4 sm:p-6 space-y-4">
                    <div>
                        <h2 className="font-heading text-lg font-bold">{t('invoices.aging_title')}</h2>
                        <p className="text-sm text-muted-foreground">{t('invoices.aging_subtitle')}</p>
                    </div>

                    {agingStatus === 'loading' ? (
                        <p className="text-sm text-muted-foreground" data-testid="aging-loading">
                            {t('invoices.aging_loading')}
                        </p>
                    ) : agingStatus === 'failed' ? (
                        /* NOT the empty state. "Nothing outstanding" is a claim
                           about the money; this is an admission that the claim
                           could not be made. Unlike a truncated read, this one
                           is actionable — so it offers the retry. */
                        <div className="space-y-2" data-testid="aging-failed-notice">
                            <p className="text-sm text-destructive">{t('invoices.aging_failed_notice')}</p>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={fetchOutstanding}
                                data-testid="aging-retry-btn"
                            >
                                {t('invoices.aging_retry')}
                            </Button>
                        </div>
                    ) : aging.totalCount === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('invoices.aging_none')}</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {aging.rows.map((row) => (
                                    <div
                                        key={row.key}
                                        className={`border border-border p-3 ${row.key === 'd60_plus' && row.count > 0 ? 'border-destructive' : ''}`}
                                        data-testid={`aging-bucket-${row.key}`}
                                    >
                                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
                                            {t(`invoices.aging_bucket_${row.key}`)}
                                        </p>
                                        <p className="font-mono text-lg font-bold mt-1">
                                            {formatAmountList(row.amounts)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {t('invoices.aging_count', { count: row.count })}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-wrap items-baseline justify-between gap-2 pt-2 border-t border-border">
                                <span className="label-uppercase text-xs text-muted-foreground">
                                    {t('invoices.aging_total')}
                                </span>
                                <span className="font-mono text-lg font-bold" data-testid="aging-total">
                                    {formatAmountList(aging.totalAmounts)}
                                </span>
                            </div>
                            {agingStatus === 'truncated' && (
                                <p className="text-xs text-destructive" data-testid="aging-truncated-notice">
                                    {t('invoices.aging_truncated_notice')}
                                </p>
                            )}
                            {/* The buckets hold `amount_due` — the balance the
                                server computed from each invoice's live
                                payments (Task 21.18), not the invoiced total.
                                A part-paid invoice now contributes only what
                                is still owed. */}
                            <p className="text-xs text-muted-foreground" data-testid="aging-balance-note">
                                {t('invoices.aging_balance_note')}
                            </p>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Filters */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                        <Label htmlFor="invoice-filter-status" className="label-uppercase mb-2 block text-xs">
                            {t('invoices.filter_status')}
                        </Label>
                        <Select value={statusFilter} onValueChange={onFilterChange(setStatusFilter)}>
                            <SelectTrigger id="invoice-filter-status" data-testid="invoice-filter-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>{t('invoices.filter_all_statuses')}</SelectItem>
                                {STORED_STATUSES.map((status) => (
                                    <SelectItem key={status} value={status}>
                                        {t(`invoices.filter_status_${status}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="invoice-filter-client" className="label-uppercase mb-2 block text-xs">
                            {t('invoices.filter_client')}
                        </Label>
                        <Select value={clientFilter} onValueChange={onFilterChange(setClientFilter)}>
                            <SelectTrigger id="invoice-filter-client" data-testid="invoice-filter-client">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>{t('invoices.filter_all_clients')}</SelectItem>
                                {clients.map((client) => (
                                    <SelectItem key={client.id} value={client.id}>
                                        {client.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="invoice-filter-doc-type" className="label-uppercase mb-2 block text-xs">
                            {t('invoices.filter_doc_type')}
                        </Label>
                        <Select value={docTypeFilter} onValueChange={onFilterChange(setDocTypeFilter)}>
                            <SelectTrigger id="invoice-filter-doc-type" data-testid="invoice-filter-doc-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>{t('invoices.filter_all_doc_types')}</SelectItem>
                                {DOC_TYPES.map((docType) => (
                                    <SelectItem key={docType} value={docType}>
                                        {t(`invoices.doc_type_${docType}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="invoice-filter-year" className="label-uppercase mb-2 block text-xs">
                            {t('invoices.filter_year')}
                        </Label>
                        <Select value={yearFilter} onValueChange={onYearChange}>
                            <SelectTrigger id="invoice-filter-year" data-testid="invoice-filter-year">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>{t('invoices.filter_all_years')}</SelectItem>
                                {yearOptions().map((year) => (
                                    <SelectItem key={year} value={String(year)}>
                                        {year}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="invoice-filter-month" className="label-uppercase mb-2 block text-xs">
                            {t('invoices.filter_month')}
                        </Label>
                        <Select
                            value={monthFilter}
                            onValueChange={onFilterChange(setMonthFilter)}
                            disabled={yearFilter === ANY}
                        >
                            <SelectTrigger id="invoice-filter-month" data-testid="invoice-filter-month">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>{t('invoices.filter_all_months')}</SelectItem>
                                {MONTHS.map((month) => (
                                    <SelectItem key={month} value={String(month)}>
                                        {t(`invoices.month_${month}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {yearFilter === ANY && (
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('invoices.filter_month_needs_year')}
                            </p>
                        )}
                    </div>

                    {filtersActive && (
                        <div className="sm:col-span-2 lg:col-span-5">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={resetFilters}
                                data-testid="clear-invoice-filters"
                            >
                                {t('invoices.clear_filters')}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Summary of the CURRENT VIEW (Task 22.7).
                Placed here, between the filters and the list it describes, and
                kept well below the accounts-receivable panel — which has its
                own heading, its own scope and its own figures. Every label
                below says "in this view" for the same reason: the two blocks
                answer different questions and neither may be read as the
                other's answer. */}
            {!loading && rows.length > 0 && (
                <div className="space-y-2" data-testid="invoices-view-summary">
                    <div>
                        <h2 className="font-heading text-lg font-bold">
                            {t('invoices.view_summary_title')}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {t('invoices.view_summary_note')}
                        </p>
                    </div>
                    <SummaryCardRow>
                        <SummaryCard
                            icon={FileText}
                            label={t('invoices.view_card_count')}
                            value={viewSummary.count}
                            valueTestId="view-count"
                        />
                        <SummaryCard
                            icon={FilePlus}
                            label={t('invoices.view_card_drafts')}
                            value={viewSummary.drafts}
                            valueTestId="view-drafts"
                        />
                        <SummaryCard
                            icon={CheckCircle2}
                            label={t('invoices.view_card_settled')}
                            value={viewSummary.settled}
                            valueTestId="view-settled"
                        />
                        <SummaryCard
                            icon={Coins}
                            label={t('invoices.view_card_invoiced')}
                            value={formatAmountList(viewSummary.amounts)}
                            variant="money"
                            valueTestId="view-invoiced-total"
                        />
                    </SummaryCardRow>
                </div>
            )}

            {/* List */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-12 flex justify-center">
                            <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin" />
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground space-y-3">
                            <FileText className="w-12 h-12 mx-auto opacity-30" />
                            <p>{filtersActive ? t('invoices.no_invoices_filtered') : t('invoices.no_invoices')}</p>
                            {filtersActive ? (
                                <Button variant="outline" size="sm" onClick={resetFilters}>
                                    {t('invoices.clear_filters')}
                                </Button>
                            ) : (
                                /* The empty list is where a first invoice gets
                                   made, so both doors are offered here too and
                                   in the same order as the header. */
                                <div className="flex flex-wrap justify-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigate('/dashboard/billing')}
                                        data-testid="empty-from-billing-btn"
                                    >
                                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                                        {t('invoices.from_billing_action')}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        {t('invoices.new_invoice')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('invoices.col_number')}</th>
                                            <th>{t('invoices.col_client')}</th>
                                            <th>{t('invoices.col_invoice_date')}</th>
                                            <th>{t('invoices.col_due_date')}</th>
                                            <th className="text-right">{t('invoices.col_total')}</th>
                                            <th>{t('invoices.col_status')}</th>
                                            <th className="text-right">{t('invoices.col_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((invoice) => (
                                            <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                                                <td className="font-mono text-sm">
                                                    <button
                                                        type="button"
                                                        className="underline underline-offset-2 hover:no-underline"
                                                        onClick={() => navigate(`/dashboard/invoices/${invoice.id}`)}
                                                        data-testid={`open-invoice-${invoice.id}`}
                                                    >
                                                        {numberCell(invoice)}
                                                    </button>
                                                    {invoice.doc_type === 'credit_note' && (
                                                        <span className="ml-2 text-xs text-muted-foreground">
                                                            {t('invoices.doc_type_credit_note')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{displayClientName(invoice)}</td>
                                                <td className="text-sm">{formatDate(invoice.invoice_date)}</td>
                                                <td className="text-sm">
                                                    {invoice.due_date
                                                        ? formatDate(invoice.due_date)
                                                        : <span className="text-muted-foreground">{t('invoices.set_at_issue')}</span>}
                                                </td>
                                                <td className="text-right font-mono">
                                                    {formatMoney(invoice.total_incl_vat, invoice.currency)}
                                                </td>
                                                <td><StatusBadge invoice={invoice} /></td>
                                                <td className="text-right whitespace-nowrap space-x-1">
                                                    {invoice.status !== 'draft' && !invoice.sent_at && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setMarkSentTarget(invoice)}
                                                            disabled={markingSentId === invoice.id}
                                                            title={t('invoices.mark_as_sent')}
                                                            aria-label={t('invoices.mark_as_sent')}
                                                            data-testid={`mark-sent-${invoice.id}`}
                                                            className="text-muted-foreground hover:text-emerald-600"
                                                        >
                                                            {markingSentId === invoice.id
                                                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                                                : <CheckCircle2 className="w-4 h-4" />}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownload(invoice)}
                                                        disabled={downloadingId === invoice.id}
                                                        aria-label={t('invoices.download_pdf')}
                                                        data-testid={`download-invoice-${invoice.id}`}
                                                    >
                                                        {downloadingId === invoice.id
                                                            ? <Loader2 className="w-4 h-4 animate-spin" />
                                                            : <Download className="w-4 h-4" />}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden p-3 space-y-3">
                                {rows.map((invoice) => (
                                    <div
                                        key={invoice.id}
                                        className="border border-border p-4 space-y-2"
                                        data-testid={`invoice-card-${invoice.id}`}
                                    >
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="min-w-0">
                                                <button
                                                    type="button"
                                                    className="font-mono font-medium underline underline-offset-2"
                                                    onClick={() => navigate(`/dashboard/invoices/${invoice.id}`)}
                                                >
                                                    {numberCell(invoice)}
                                                </button>
                                                <p className="text-sm truncate">{displayClientName(invoice)}</p>
                                            </div>
                                            <StatusBadge invoice={invoice} />
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {formatDate(invoice.invoice_date)}
                                            {invoice.due_date ? ` · ${t('invoices.col_due_date')}: ${formatDate(invoice.due_date)}` : ''}
                                        </p>
                                        <div className="flex items-center justify-between pt-2 border-t border-border">
                                            <span className="font-mono font-bold">
                                                {formatMoney(invoice.total_incl_vat, invoice.currency)}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                {invoice.status !== 'draft' && !invoice.sent_at && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="min-h-[44px] min-w-[44px] p-0 text-muted-foreground hover:text-emerald-600"
                                                        onClick={() => setMarkSentTarget(invoice)}
                                                        disabled={markingSentId === invoice.id}
                                                        title={t('invoices.mark_as_sent')}
                                                        aria-label={t('invoices.mark_as_sent')}
                                                    >
                                                        {markingSentId === invoice.id
                                                            ? <Loader2 className="w-4 h-4 animate-spin" />
                                                            : <CheckCircle2 className="w-4 h-4" />}
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="min-h-[44px] min-w-[44px] p-0"
                                                    onClick={() => handleDownload(invoice)}
                                                    disabled={downloadingId === invoice.id}
                                                    aria-label={t('invoices.download_pdf')}
                                                >
                                                    {downloadingId === invoice.id
                                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                                        : <Download className="w-4 h-4" />}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Paging — the list endpoint returns rows, not a count, so "there
                is another page" is inferred from a full page having come back. */}
            {(page > 0 || rows.length === PAGE_SIZE) && (
                <div className="flex items-center justify-between">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 0 || loading}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        data-testid="invoices-prev-page"
                    >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        {t('common.previous')}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                        {t('invoices.page_number', { page: page + 1 })}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={rows.length < PAGE_SIZE || loading}
                        onClick={() => setPage((p) => p + 1)}
                        data-testid="invoices-next-page"
                    >
                        {t('common.next')}
                        <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            )}

            {/* New draft */}
            <Dialog open={!!markSentTarget} onOpenChange={(open) => !open && setMarkSentTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('invoices.mark_as_sent_confirm_title')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('invoices.mark_as_sent_confirm_desc')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMarkSentTarget(null)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={async () => {
                                const target = markSentTarget;
                                setMarkSentTarget(null);
                                if (target) await handleMarkSent(target);
                            }}
                            disabled={markingSentId !== null}
                            data-testid="confirm-mark-sent-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {markingSentId !== null && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {t('invoices.mark_as_sent_confirm_action')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {t('invoices.new_invoice_title')}
                        </DialogTitle>
                        <DialogDescription>{t('invoices.new_invoice_body')}</DialogDescription>
                    </DialogHeader>

                    {/* The last moment before the wrong door closes behind you:
                        this dialog is where someone billing a month of logged
                        time ends up if they never saw the billing report
                        (Task 22.8). */}
                    <p className="text-xs text-muted-foreground" data-testid="new-invoice-manual-note">
                        {t('invoices.new_invoice_manual_note')}
                    </p>

                    {activeClients.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('invoices.no_clients')}</p>
                    ) : (
                        <div>
                            <Label htmlFor="new-invoice-client" className="label-uppercase mb-2 block">
                                {t('invoices.new_invoice_client')}
                            </Label>
                            <Select value={createClientId} onValueChange={setCreateClientId}>
                                <SelectTrigger id="new-invoice-client" data-testid="new-invoice-client">
                                    <SelectValue placeholder={t('invoices.select_client')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {activeClients.map((client) => (
                                        <SelectItem key={client.id} value={client.id}>
                                            {client.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={creating || !createClientId || activeClients.length === 0}
                            data-testid="create-invoice-confirm"
                        >
                            {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {t('invoices.create_draft')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
